import React, {
  ReactElement, useMemo, useState, useRef, useEffect,
} from 'react'
import {
  Row, Col, Card, Table, Button,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { downloadCSV } from 'utils/csvExport'

interface EnterpriseSurveyReportRecipientsProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: string | null, direction: 'asc' | 'desc') => void
}

export const EnterpriseSurveyReportRecipients: React.FC<EnterpriseSurveyReportRecipientsProps> = ({
  filteredRecipients,
  sortColumn,
  sortDirection,
  onSortChange,
}): ReactElement => {
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      onSortChange(column, sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      onSortChange(column, 'asc')
    }
  }

  const renderSortIcon = (column: string) => {
    const isActive = sortColumn === column

    if (isActive) {
      // Show only the active direction icon when sorted
      return (
        <span className="ml-2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <FontAwesomeIcon
            icon={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
            style={{ fontSize: '0.75rem', opacity: 1 }}
          />
        </span>
      )
    }

    // Show chevron right when not sorted to indicate sortability
    return (
      <span className="ml-2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <FontAwesomeIcon
          icon="chevron-right"
          style={{ fontSize: '0.75rem', opacity: 1 }}
        />
      </span>
    )
  }
  // Get unique recipients (one row per recipient, showing their response status)
  const uniqueRecipients = useMemo(() => {
    const recipientMap = new Map<string, IBroadcastFormRecipientResponseReportRow>()

    filteredRecipients.forEach((recipient) => {
      const key = `${recipient.ProfileId}-${recipient.FormSessionId}`
      if (!recipientMap.has(key)) {
        recipientMap.set(key, recipient)
      } else {
        // If we already have this recipient, keep the one with HasResponse=true if available
        const existing = recipientMap.get(key)
        if (existing && !existing.HasResponse && recipient.HasResponse) {
          recipientMap.set(key, recipient)
        }
      }
    })

    return Array.from(recipientMap.values())
  }, [filteredRecipients])

  const metrics = useMemo(() => {
    const totalRecipients = uniqueRecipients.length
    const responses = uniqueRecipients.filter((r) => r.HasResponse).length
    const unresponses = totalRecipients - responses
    const responseRate = totalRecipients > 0 ? (responses / totalRecipients) * 100 : 0

    return {
      totalRecipients,
      responses,
      unresponses,
      responseRate,
    }
  }, [uniqueRecipients])

  // Virtual scrolling state
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const ROW_HEIGHT = 50
  const CONTAINER_HEIGHT = 600

  // Calculate visible range based on scroll position
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return undefined
    }

    const handleScroll = () => {
      const { scrollTop } = container
      const start = Math.floor(scrollTop / ROW_HEIGHT)
      const visibleCount = Math.ceil(CONTAINER_HEIGHT / ROW_HEIGHT)
      const end = Math.min(start + visibleCount + 5, uniqueRecipients.length) // +5 for buffer

      setVisibleRange({ start: Math.max(0, start - 2), end }) // -2 for buffer above
    }

    container.addEventListener('scroll', handleScroll)
    handleScroll() // Initial calculation

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [uniqueRecipients.length])

  // Get visible rows
  const visibleRows = useMemo(
    () => uniqueRecipients.slice(visibleRange.start, visibleRange.end),
    [uniqueRecipients, visibleRange],
  )

  const handleDownloadCSV = () => {
    const headers = ['Name', 'User Type', 'Account', 'Form Type', 'Form Name', 'Response Status', 'Response Date']

    const rows = uniqueRecipients.map((recipient) => {
      const name = `${recipient.FirstName} ${recipient.LastName}`.trim()
      const userType = recipient.UserType || ''
      const account = recipient.Account_Name || ''
      const formType = recipient.FormType || ''
      const formName = recipient.Name || ''
      const responseStatus = recipient.HasResponse ? 'Responded' : 'No Response'
      const responseDate = recipient.ResponseAtUtc
        ? new Date(recipient.ResponseAtUtc).toLocaleString()
        : ''

      return [name, userType, account, formType, formName, responseStatus, responseDate]
    })

    downloadCSV(headers, rows, 'recipients')
  }

  return (
    <div>
      <Row className="mt-4 mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Total Sent</Card.Title>
              <Card.Text className="h3 mb-0">{metrics.totalRecipients.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Responded</Card.Title>
              <Card.Text className="h3 mb-0 text-success">{metrics.responses.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Not Responded</Card.Title>
              <Card.Text className="h3 mb-0 text-warning">{metrics.unresponses.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Response Rate</Card.Title>
              <Card.Text className="h3 mb-0 text-primary">
                {metrics.responseRate.toFixed(1)}
                %
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Recipient Details</h5>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={uniqueRecipients.length === 0}
            >
              <FontAwesomeIcon icon="download" className="mr-2" />
              Download CSV
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {uniqueRecipients.length === 0 ? (
            <div className="text-center text-muted p-4">
              No recipients found
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              style={{
                position: 'relative',
                height: `${CONTAINER_HEIGHT}px`,
                overflow: 'auto',
              }}
            >
              <Table
                bordered
                hover
                responsive
                style={{
                  margin: 0,
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                }}
                >
                  <tr>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >
                      Name
                      {renderSortIcon('name')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('userType')}
                    >
                      User Type
                      {renderSortIcon('userType')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('account')}
                    >
                      Account
                      {renderSortIcon('account')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('formType')}
                    >
                      Form Type
                      {renderSortIcon('formType')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('formName')}
                    >
                      Form Name
                      {renderSortIcon('formName')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('responseStatus')}
                    >
                      Response Status
                      {renderSortIcon('responseStatus')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('responseDate')}
                    >
                      Response Date
                      {renderSortIcon('responseDate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Spacer for rows above visible range */}
                  {visibleRange.start > 0 && (
                    <tr style={{ height: `${visibleRange.start * ROW_HEIGHT}px` }}>
                      <td colSpan={7} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                  {/* Visible rows */}
                  {visibleRows.map((recipient, visibleIndex) => {
                    // Calculate actual index in full dataset for consistent striping
                    const actualIndex = visibleRange.start + visibleIndex
                    const isEven = actualIndex % 2 === 0
                    return (
                      <tr
                        key={`${recipient.ProfileId}-${recipient.FormSessionId}`}
                        className={isEven ? 'table-active' : ''}
                      >
                        <td>
                          {recipient.FirstName}
                          {' '}
                          {recipient.LastName}
                        </td>
                        <td>{recipient.UserType || '-'}</td>
                        <td>{recipient.Account_Name || '-'}</td>
                        <td>{recipient.FormType || '-'}</td>
                        <td>{recipient.Name || '-'}</td>
                        <td>
                          {recipient.HasResponse ? (
                            <span className="text-success">Responded</span>
                          ) : (
                            <span className="text-warning">No Response</span>
                          )}
                        </td>
                        <td>
                          {recipient.ResponseAtUtc
                            ? new Date(recipient.ResponseAtUtc).toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    )
                  })}
                  {/* Spacer for rows below visible range */}
                  {visibleRange.end < uniqueRecipients.length && (
                    <tr style={{ height: `${(uniqueRecipients.length - visibleRange.end) * ROW_HEIGHT}px` }}>
                      <td colSpan={7} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
